/**
 * Training Video Service
 *
 * Fetches video metadata and playback URLs from the backend API.
 * The backend generates short-lived SAS tokens via Managed Identity.
 *
 * Two endpoints:
 *   GET /api/v1/training-videos?user_email=...           → list all videos
 *   GET /api/v1/training-videos/{video_id}/url?user_email=... → signed playback URL
 */

import { httpClient, unwrapApiResult } from './authenticatedHttpClient';
import { apiPath } from '../config/env';
import { authManager } from './authManager';
import { encodeEmailForUrl } from '../utils/emailUtils';
import type { TrainingVideo } from '../types/training';

// ---------------------------------------------------------------------------
// Backend response interfaces
// ---------------------------------------------------------------------------

interface BackendVideoSummary {
    video_id: string;
    title: string;
    description: string;
    duration_seconds: number;
    category: string;
    sort_order: number;
    thumbnail_filename: string | null;
}

interface BackendVideoListResponse {
    videos: BackendVideoSummary[];
}

interface BackendVideoUrlResponse {
    video_id: string;
    title: string;
    description: string;
    duration_seconds: number;
    category: string;
    url: string;
    expires_at: string;
    content_type: string;
}

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

function toTrainingVideo(v: BackendVideoSummary): TrainingVideo {
    return {
        id: v.video_id,
        title: v.title,
        description: v.description,
        durationSeconds: v.duration_seconds,
        category: v.category,
        sortOrder: v.sort_order,
        thumbnailFilename: v.thumbnail_filename ?? null,
    };
}

// ---------------------------------------------------------------------------
// In-memory caches
// ---------------------------------------------------------------------------

/** Cached signed URL: videoId → { url, expiresAt } */
const urlCache = new Map<string, { url: string; expiresAt: Date }>();
const EXPIRY_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Fetch the full list of training videos with metadata from the backend.
 */
export async function fetchTrainingVideoList(): Promise<TrainingVideo[]> {
    const email = encodeEmailForUrl(authManager.getUserEmail());
    const path = apiPath(`training-videos?user_email=${email}`);
    const result = await httpClient.get<BackendVideoListResponse>(path);
    const data = unwrapApiResult(result);
    return data.videos.map(toTrainingVideo);
}

/**
 * Get a signed playback URL for a training video.
 *
 * The URL is set directly as <video src> (no JS fetch, no CORS issue).
 * Returns the URL along with video metadata.
 */
export async function getTrainingVideoUrl(videoId: string): Promise<{
    url: string;
    video: TrainingVideo;
}> {
    // Return cached URL if still valid
    const cached = urlCache.get(videoId);
    if (cached && cached.expiresAt.getTime() > Date.now() + EXPIRY_BUFFER_MS) {
        // We still need metadata — but the URL is reusable.
        // Caller uses this URL; metadata comes from the list.
        // For standalone use (ContextualHelpLink), re-fetch for metadata.
    } else {
        // Fetch fresh signed URL + metadata
        const email = encodeEmailForUrl(authManager.getUserEmail());
        const path = apiPath(`training-videos/${videoId}/url?user_email=${email}`);
        const result = await httpClient.get<BackendVideoUrlResponse>(path);
        const data = unwrapApiResult(result);

        urlCache.set(videoId, {
            url: data.url,
            expiresAt: new Date(data.expires_at),
        });

        return {
            url: data.url,
            video: {
                id: data.video_id,
                title: data.title,
                description: data.description,
                durationSeconds: data.duration_seconds,
                category: data.category,
                sortOrder: 0,
                thumbnailFilename: null,
            },
        };
    }

    // Cached URL — still need metadata, fetch it
    const email = encodeEmailForUrl(authManager.getUserEmail());
    const path = apiPath(`training-videos/${videoId}/url?user_email=${email}`);
    const result = await httpClient.get<BackendVideoUrlResponse>(path);
    const data = unwrapApiResult(result);

    urlCache.set(videoId, {
        url: data.url,
        expiresAt: new Date(data.expires_at),
    });

    return {
        url: data.url,
        video: {
            id: data.video_id,
            title: data.title,
            description: data.description,
            durationSeconds: data.duration_seconds,
            category: data.category,
            sortOrder: 0,
            thumbnailFilename: null,
        },
    };
}
